import isEqual from 'lodash/isEqual';
import { useEffect, useMemo } from 'react';
import type React from 'react';
import { useSlate } from 'slate-react';

import { EditorWrapper } from '@xing-com/content-editor-core-editor';
import {
  Plugin as BoldPlugin,
  renderer as boldRenderer,
} from '@xing-com/content-editor-plugin-bold';
import {
  Plugin as ItalicPlugin,
  renderer as italicRenderer,
} from '@xing-com/content-editor-plugin-italic';
import {
  Plugin as ListItemPlugin,
  renderer as listItemRenderer,
} from '@xing-com/content-editor-plugin-list-item';
import {
  Plugin as ParagraphPlugin,
  renderer as paragraphRenderer,
} from '@xing-com/content-editor-plugin-paragraph';
import type { VitaCoverLetterText } from '@xing-com/crate-common-graphql-types';
import {
  useCoverLetterActions,
  useReadCoverLetter,
} from '@xing-com/crate-lebenslauf-hooks';

import { StyledEditor } from './cover-letter-editor.styled';

const SyncCoverLetterContent: React.FC = () => {
  const editor = useSlate();
  const { content } = useReadCoverLetter();

  useEffect(() => {
    if (!isEqual(content, editor.children)) {
      editor.children = content as any;
      editor.selection = {
        anchor: { path: [0, 0], offset: 0 },
        focus: { path: [0, 0], offset: 0 },
      };
      editor.onChange();
    }
  }, [content]);

  return null;
};

const bold = (props: any): void =>
  boldRenderer({ ...props, 'data-testid': 'testIDs.Bold' });
const italic = (props: any): void =>
  italicRenderer({ ...props, 'data-testid': 'testIDs.Italic' });
const listItem = (props: any): void =>
  listItemRenderer({
    ...props,
    'data-testid': 'testIDs.ListItem',
  });
const paragraph = (props: any): void =>
  paragraphRenderer({
    ...props,
    'data-testid': 'testIDs.Paragraph',
  });
export const RENDERERS = [bold, italic, listItem, paragraph];

export const CoverLetterEditor: React.FC = () => {
  const coverLetter = useReadCoverLetter();
  const { content } = coverLetter;
  const invalidFormInputNames =
    coverLetter.validation?.invalidFormInputNames ?? [];
  const isValid = !invalidFormInputNames.includes('content');
  const { coverLetterDocumentActions, coverLetterValidationActions } =
    useCoverLetterActions();
  const EDITOR_PLUGINS = useMemo(
    () => [
      BoldPlugin({ 'data-testid': 'testIDs.Bold' }),
      ItalicPlugin({ 'data-testid': 'testIDs.Italic' }),
      ListItemPlugin({ 'data-testid': 'testIDs.ListItem' }),
      ParagraphPlugin({
        'data-testid': 'testIDs.Paragraph',
      }),
    ],
    []
  );
  const contentArray = coverLetter.content?.flatMap(({ children }) =>
    (children as VitaCoverLetterText[])?.map(({ text }) => text)
  );
  const hasContent = contentArray?.find((paragraph) => paragraph.length);

  const handleChange = (value: any): void => {
    coverLetterDocumentActions({
      type: 'edit-content' as const,
      value,
    });
  };

  const handleBlur = (): void => {
    coverLetterValidationActions({
      type: 'delete-invalid-form-input-name' as const,
      value: 'content',
    });
  };

  return (
    <EditorWrapper
      onChange={handleChange}
      initialValue={content}
      plugins={EDITOR_PLUGINS}
    >
      <SyncCoverLetterContent />
      <StyledEditor
        name="content"
        onBlur={handleBlur}
        plugins={EDITOR_PLUGINS}
        $isValid={isValid}
        $hasContent={hasContent}
      />
    </EditorWrapper>
  );
};
